import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X } from 'lucide-react';
import Input from '../input';
import Button from '../button';
import SearchSelect from '../search-select';
import useRequest from '../hooks/use-req';
import { showToast } from '../toast';
import { ClockLoader } from 'react-spinners';
import { Bank, BankAccount, BankAccountPopupProps, BankAccountsResponse, FormData } from '../../pages/recommender/wallet/types';
import { AnimatedCheckIcon } from '../animation-check';
import PayoutPopup from './payout-modal';
import BankSelect from '../bank-select';

const BankAccountPopup: React.FC<BankAccountPopupProps> = ({
  isOpen,
  onClose,
  title = 'Link account',
  subtitle = 'Choose a bank to link your account'
}) => {
  const authToken = localStorage.getItem("authToken");
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [selectedBank, setSelectedBank] = useState<{ name: string; code: string } | null>(null);
  const [userAccount, setUserAccount] = useState<BankAccountsResponse | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const [isPayoutOpen, setIsPayoutOpen] = useState(false);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [selectedLinkedAccountId, setSelectedLinkedAccountId] = useState<string>('');
  const [isFirstAccountLinking, setIsFirstAccountLinking] = useState(false);

  const { makeRequest: linkAccount, loading } = useRequest("/bank/bank/account", "POST", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: switchAccount, loading: switchLoader } = useRequest(`/bank/bank/${selectedLinkedAccountId}/default`, "PATCH", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: validateAccount, loading: validationLoading } = useRequest(
    "/bank/bank/validate-account",
    "POST",
    {
      Authorization: `Bearer ${authToken}`,
    }
  );

  const { makeRequest: getBanks } = useRequest(
    "/bank/bank/",
    "GET",
    {
      Authorization: `Bearer ${authToken}`,
    }
  );

  const { makeRequest: getUserAccounts } = useRequest("/bank/bank/accounts", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const handlePayoutOpenPopup = () => setIsPayoutOpen(true);
  const handlePayoutClosePopup = () => setIsPayoutOpen(false);
  const { handleSubmit, control, reset, setValue, watch } = useForm<FormData>();

  // Watch form values for validation
  const accountNumber = watch('account_number');
  const bankCode = watch('bank_code');

  // Fetch user's linked accounts
  useEffect(() => {
    const fetchUserAccounts = async () => {
      if (!isOpen) return;
      const [response] = await getUserAccounts();
      if (response?.bank_accounts) {
        setUserAccount(response);
      }
    };

    if (isOpen) {
      fetchUserAccounts();
    }
  }, [isOpen]);

  // Account options for linked accounts dropdown
  const accountOptions = userAccount?.bank_accounts?.map((account: BankAccount) => {
    const isDefault = String(userAccount?.default_account?.id) === String(account.id);
    return {
      value: String(account.id),
      label: `${account.account_number} • ${account.bank_name}`,
      subLabel: account.account_name,
      isDefault,
    };
  }) || [];

  const hasLinkedAccounts = accountOptions.length > 0;

  // Dynamic title and subtitle based on whether user has linked accounts
  const getTitle = () => {
    if (hasLinkedAccounts && !showAddNewForm) {
      return 'Switch Accounts';
    }
    return title;
  };

  const getSubtitle = () => {
    if (hasLinkedAccounts && !showAddNewForm) {
      return 'Switch to your preferred payout method to receive your earnings.';
    }
    return subtitle;
  };

  useEffect(() => {
    const fetchBanks = async () => {
      if (!isOpen) return;

      setLoadingBanks(true);

      const [response] = await getBanks();

      if (response?.status) {
        const sortedBanks = response?.data
          .map((bank: any) => ({
            id: bank.id,
            code: bank.code,
            name: bank.name,
          }))
          .sort((a: Bank, b: Bank) => a.name.localeCompare(b.name));

        setBanks(sortedBanks);
      } else {
        showToast(response?.message || "Failed to load banks", false);
      }

      setLoadingBanks(false);
    };

    fetchBanks();
  }, [isOpen]);

  // Validate account when bank and account number are both entered
  useEffect(() => {
    const validateBankAccount = async () => {
      if (!bankCode || !accountNumber || accountNumber.length !== 10) {
        setValidationError('');
        return;
      }
      setValidationError('');
      const bankDetails = {
        bank_code: bankCode,
        account_number: accountNumber
      }
      const [validationResponse] = await validateAccount(bankDetails);

      if (validationResponse?.success) {
        // Auto-populate account name if validation is successful
        if (validationResponse?.account_name) {
          setValue('account_name', validationResponse?.account_name);
        }
        setValidationError('');
      } else {
        setValidationError(validationResponse?.message || 'Invalid bank account details');
      }
    };

    // Add a small delay to avoid excessive API calls
    const timer = setTimeout(() => {
      validateBankAccount();
    }, 500);

    return () => clearTimeout(timer);
  }, [bankCode, accountNumber, setValue]);

  const handleLinkAccount = handleSubmit(async (formData: FormData) => {
    if (!selectedBank) {
      showToast('Please select a bank', false);
      return;
    }

    // Check if there's a validation error
    if (validationError) {
      showToast('Please fix the account validation error before submitting', false);
      return;
    }

    const linkBankPayload = {
      bank_name: selectedBank.name,
      bank_code: selectedBank.code,
      account_name: formData.account_name,
      account_number: formData.account_number,
    };

    const [linkResponse, error] = await linkAccount(linkBankPayload);

    if (linkResponse?.status) {
      showToast(linkResponse?.message, true);
      reset();
      setSuccess(true);
      setSelectedBank(null);
      setValidationError('');
      setShowAddNewForm(false);
      setSelectedLinkedAccountId('');
      
      // Check if this was the first account being linked
      setIsFirstAccountLinking(!hasLinkedAccounts);

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } else {
      showToast(error?.message || 'Failed to link account', false);
    }
  });

  const handleSwitchAccount = async () => {
    if (!selectedLinkedAccountId) {
      showToast('Please select an account to switch to', false);
      return;
    }

    const switchPayload = {
      bank_id: selectedLinkedAccountId
    };

    const [switchResponse] = await switchAccount(switchPayload);

    if (switchResponse?.status) {
      showToast(switchResponse?.message || 'Account switched successfully', true);
      reset();
      setSuccess(true);
      setSelectedBank(null);
      setValidationError('');
      setShowAddNewForm(false);
      setSelectedLinkedAccountId('');
      setIsFirstAccountLinking(false); // Not first account when switching

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } else {
      showToast(switchResponse?.message || 'Failed to switch account', false);
    }
  };

  const handleBankSelect = (value: string) => {
    const selected = banks.find(bank => bank.name === value);
    if (selected) {
      setSelectedBank({
        name: selected.name,
        code: selected.code
      });
      setValue('bank_name', selected.name);
      setValue('bank_code', selected.code);
      setValidationError(''); // Clear validation error when bank changes
    }
  };

  const handleLinkedAccountSelect = (value: string) => {
    setSelectedLinkedAccountId(value);
    const selected = userAccount?.bank_accounts?.find(
      (account) => account.id.toString() === value
    );
    if (selected) {
      setValue("bank_name", selected.bank_name);
      setValue("account_name", selected.account_name);
      setValue("account_number", selected.account_number);
      setSelectedBank({
        name: selected.bank_name,
        code: selected.bank_code || '' // Assuming bank_code is part of the account data
      });
    }
  };

  const handleAddNewClick = () => {
    setShowAddNewForm(true);
    setSelectedLinkedAccountId('');
    reset();
    setSelectedBank(null);
    setValidationError('');
  };

  const handleCancelAddNew = () => {
    setShowAddNewForm(false);
    reset();
    setSelectedBank(null);
    setValidationError('');
    setSelectedLinkedAccountId('');
  };

  const handleClose = () => {
    reset();
    setSelectedBank(null);
    setValidationError('');
    setSuccess(false);
    setShowAddNewForm(false);
    setSelectedLinkedAccountId('');
    setIsFirstAccountLinking(false);
    onClose();
  };

  const handleRedirectToExplore = () => {
    window.location.href = '/';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Close popup"
        >
          <X size={20} />
        </button>

        {success ? (
          // ─── SUCCESS SCREEN ───
          <div className="py-8 px-4 w-full text-center">
            <h2 className="text-2xl font-bold text-[#121212] mb-3">
              Your Account Is Securely Linked
            </h2>
            <p className="text-[#65799A] text-[14px] mb-8 leading-relaxed">
              Your bank account is now connected and ready for payouts.
            </p>
            <div className="flex justify-center items-center mb-6">
              <AnimatedCheckIcon />
            </div>
            
            {/* Show different buttons based on whether it's first account linking */}
            {isFirstAccountLinking ? (
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                type="button"
                onClick={handleRedirectToExplore}
              >
                Explore Rewards
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                type="button"
                 onClick={handleClose}
              >
                Close
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Header with dynamic title and subtitle */}
            <div className="mb-6">
              <h2 className="text-[18px] font-bold text-[#121212]">{getTitle()}</h2>
              <p className="text-[14px] text-[#65799A] font-medium mt-1">{getSubtitle()}</p>
            </div>

            {/* Show linked accounts dropdown if user has linked accounts and not in add new mode */}
            {hasLinkedAccounts && !showAddNewForm && (
              <div className="mb-6">
                <Controller
                  name="selectedAccount"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <BankSelect
                        {...field}
                        label="Choose a linked account"
                        options={accountOptions}
                        className="w-full"
                        onChange={(value) => {
                          field.onChange(value);
                          handleLinkedAccountSelect(value);
                        }}
                        value={field.value}
                      />
                    </div>
                  )}
                />
              </div>
            )}

            {/* Show add new account link or cancel button */}
            {hasLinkedAccounts && !showAddNewForm && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleAddNewClick}
                  className="text-[#767994] text-[14px] font-medium underline"
                >
                  Add New Account
                </button>
              </div>
            )}

            {/* Form for adding new account */}
            {(showAddNewForm || !hasLinkedAccounts) && (
              <>
                {/* {hasLinkedAccounts && showAddNewForm && (
                  <div className="mb-4 flex justify-between items-center">
                    <h3 className='text-[#121212] font-bold text-[14px]'>
                      Add New Account
                    </h3>
                    <button
                      type="button"
                      onClick={handleCancelAddNew}
                      className="text-[#65799A] text-[12px] hover:text-[#121212]"
                    >
                      Cancel
                    </button>
                  </div>
                )} */}

                <form onSubmit={handleLinkAccount}>
                  {/* Bank Selection */}
                  <div className="mb-4">
                    <Controller
                      name="bank_name"
                      control={control}
                      rules={{ required: 'Bank name is required' }}
                      render={({ field, fieldState }) => (
                        <div>
                          <SearchSelect
                            {...field}
                            label="Bank Name"
                            options={banks.map((bank) => ({
                              value: bank.name,
                              label: bank.name,
                            }))}
                            className="w-full"
                            onChange={(value) => {
                              field.onChange(value);
                              handleBankSelect(value);
                            }}
                            value={field.value}
                            disabled={loadingBanks}
                          />
                          {fieldState.error && (
                            <p className="mt-1 text-sm text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    {/* Hidden field for bank_code */}
                    <Controller
                      name="bank_code"
                      control={control}
                      render={({ field }) => (
                        <input type="hidden" {...field} />
                      )}
                    />
                  </div>

                  {/* Account Number */}
                  <div className="mb-4">
                    <Controller
                      name="account_number"
                      control={control}
                      rules={{
                        required: 'Account number is required',
                        pattern: {
                          value: /^\d{10}$/,
                          message: 'Account number must be 10 digits'
                        }
                      }}
                      render={({ field, fieldState }) => (
                        <div>
                          <Input
                            {...field}
                            type="text"
                            placeholder="10-digit account number"
                            label='Account Number'
                            className="w-full"
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '')?.slice(0, 10);
                              field.onChange(value);
                            }}
                          />
                          {fieldState.error && (
                            <p className="mt-1 text-sm text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                          {/* Validation status indicator */}
                          {field.value && field.value.length === 10 && bankCode && (
                            <div className="mt-2">
                              {validationLoading ? (
                                <div className="flex items-center text-sm text-gray-500">
                                  <ClockLoader size={12} className="mr-2" />
                                  Validating account...
                                </div>
                              ) : validationError ? (
                                <p className="text-sm text-red-600 flex items-center">
                                  <span className="mr-1">⚠</span>
                                  {validationError}
                                </p>
                              ) : (
                                <p className="text-sm text-green-600 flex items-center">
                                  <span className="mr-1">✓</span>
                                  Account validated successfully
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Account Holder Name */}
                  <div className="mb-6">
                    <Controller
                      name="account_name"
                      control={control}
                      rules={{
                        required: 'Account name is required',
                        pattern: {
                          value: /^[A-Za-z\s]+$/,
                          message: 'Name should contain only letters and spaces'
                        }
                      }}
                      render={({ field, fieldState }) => (
                        <div>
                          <Input
                            {...field}
                            type="text"
                            placeholder="Account holder name"
                            className="w-full"
                            label='Name on Bank Account'
                          />
                          {fieldState.error && (
                            <p className="mt-1 text-sm text-red-600">
                              {fieldState.error.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full mb-3"
                      type="submit"
                      disabled={loading || loadingBanks || !selectedBank || !!validationError || validationLoading}
                    >
                      {loading ? (
                        <ClockLoader color="#ffffff" loading={loading} size={20} />
                      ) : (
                        <>Link Bank Account</>
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}

            {/* Save Changes button for switching accounts */}
            {hasLinkedAccounts && !showAddNewForm && selectedLinkedAccountId && (
              <div className="flex space-x-3 mt-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full mb-3"
                  type="button"
                  onClick={handleSwitchAccount}
                  disabled={switchLoader || !selectedLinkedAccountId}
                >
                  {switchLoader ? (
                    <ClockLoader color="#ffffff" loading={switchLoader} size={20} />
                  ) : (
                    <>Save Changes</>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <PayoutPopup
        isOpen={isPayoutOpen}
        onClose={handlePayoutClosePopup}
      />
    </div>
  );
};

export default BankAccountPopup;